import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import { useCaregiverActivityDetail } from "../../../hooks/useCaregiverActivity";
import "./CareCountSection.css";

const WINDOW_ROWS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "180d", label: "180 days" },
  { key: "360d", label: "360 days" },
];

/** weekStart is YYYY-MM-DD (Sunday) in local calendar — avoid UTC shift to Saturday. */
const parseLocalDate = (isoDate) => {
  const parts = String(isoDate || "").split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    return new Date(isoDate);
  }
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const formatWeekLabel = (isoDate) => {
  const d = parseLocalDate(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  const weekLabel = row.weekStart
    ? formatWeekLabel(row.weekStart)
    : row.week || "";
  return (
    <div className="care-count-tooltip">
      <div className="care-count-tooltip-week">Week of {weekLabel} (Sun–Sat)</div>
      <div className="care-count-tooltip-value">
        {payload[0].value} visit{payload[0].value === 1 ? "" : "s"}
      </div>
    </div>
  );
};

const CareCountSection = ({ clientId }) => {
  const { data, isLoading, isError, isFetching, refreshFromWellSky } =
    useCaregiverActivityDetail(clientId);
  const showInitialLoading = isLoading && !data;
  const [caregiverWindow, setCaregiverWindow] = useState("90d");
  const [manualSyncing, setManualSyncing] = useState(false);

  const chartData = (data?.weeklySeries || []).map((w) => ({
    week: formatWeekLabel(w.weekStart),
    weekStart: w.weekStart,
    visits: w.visitCount,
  }));

  const caregiverBreakdown =
    data?.caregiverBreakdownByWindow?.[caregiverWindow] || [];
  const caregiverWindowLabel =
    WINDOW_ROWS.find((w) => w.key === caregiverWindow)?.label || "90 days";

  return (
    <div className="care-count-section">
      <div className="care-count-header">
        <div>
          <h2 className="care-count-title">
            <FontAwesomeIcon icon={faUsers} style={{ marginRight: 8 }} />
            Care Count
          </h2>
          <p className="care-count-subtitle">
            Visit frequency and caregiver coverage from WellSky shifts (includes
            scheduled shifts through the end of this month). Uses synced WellSky
            data; click Update from WellSky to pull the latest shifts for this client.
          </p>
        </div>
        <button
          type="button"
          className="care-count-window-filter-btn"
          disabled={isFetching || manualSyncing}
          onClick={async () => {
            setManualSyncing(true);
            try {
              await refreshFromWellSky();
            } finally {
              setManualSyncing(false);
            }
          }}
        >
          {isFetching || manualSyncing ? "Updating…" : "Update from WellSky"}
        </button>
      </div>

      {isError ? (
        <div className="care-count-empty">
          Couldn&apos;t load caregiver activity for this client.
        </div>
      ) : (
        <>
          <div className="care-count-tiles">
            {WINDOW_ROWS.map((w) => {
              const row = data?.windows?.[w.key] || {
                visitCount: 0,
                daysWithVisit: 0,
                caregiverCount: 0,
              };
              return (
                <div key={w.key} className="care-count-tile">
                  <span className="care-count-tile-window">{w.label}</span>
                  {showInitialLoading ? (
                    <span className="care-count-tile-loading">Loading…</span>
                  ) : (
                    <div className="care-count-tile-stats">
                      <div className="care-count-tile-stat">
                        <span className="care-count-tile-stat-value">
                          {row.visitCount}
                        </span>
                        <span className="care-count-tile-stat-label">Visits</span>
                      </div>
                      <div className="care-count-tile-stat">
                        <span className="care-count-tile-stat-value">
                          {row.daysWithVisit}
                        </span>
                        <span className="care-count-tile-stat-label">Days</span>
                      </div>
                      <div className="care-count-tile-stat">
                        <span className="care-count-tile-stat-value">
                          {row.caregiverCount}
                        </span>
                        <span className="care-count-tile-stat-label">
                          Caregivers
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="care-count-panel">
            <div className="care-count-panel-header">
              <h3 className="care-count-panel-title">
                Weekly Visits — last 13 weeks (Sun–Sat)
              </h3>
            </div>
            <div className="care-count-chart-wrap">
              {showInitialLoading ? (
                <div className="care-count-empty">Loading chart…</div>
              ) : chartData.every((d) => d.visits === 0) ? (
                <div className="care-count-empty">
                  No visits recorded in the last 13 weeks.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={{ stroke: "#e2e8f0" }}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="visits" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="care-count-panel">
            <div className="care-count-panel-header care-count-panel-header-row">
              <h3 className="care-count-panel-title">
                <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: 8 }} />
                Caregivers — last {caregiverWindowLabel}
              </h3>
              <div className="care-count-window-filter" role="group" aria-label="Select time window">
                {WINDOW_ROWS.map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    className={
                      "care-count-window-filter-btn" +
                      (w.key === caregiverWindow ? " active" : "")
                    }
                    onClick={() => setCaregiverWindow(w.key)}
                  >
                    {w.key}
                  </button>
                ))}
              </div>
            </div>
            {showInitialLoading ? (
              <div className="care-count-empty">Loading caregivers…</div>
            ) : caregiverBreakdown.length === 0 ? (
              <div className="care-count-empty">
                No caregiver visits recorded in the last {caregiverWindowLabel}.
              </div>
            ) : (
              <table className="care-count-caregiver-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Role</th>
                    <th>Visits ({caregiverWindow})</th>
                  </tr>
                </thead>
                <tbody>
                  {caregiverBreakdown.map((c, index) => (
                    <tr
                      key={`${c.providerName || "unknown"}-${c.providerRole || "Caregiver"}-${index}`}
                    >
                      <td>{c.providerName}</td>
                      <td>{c.providerRole || "Caregiver"}</td>
                      <td>{c.visitCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CareCountSection;
